import moment from 'moment';
import * as React from 'react';
import { Button, Input } from 'reactstrap';
import {
  copyDate,
  END_TIME_INPUT_STATE,
  getValidTime,
  INITIAL_INPUT_STATE,
  initializeTimes,
  START_TIME_INPUT_STATE,
} from '../../../../constants';
import { InputState, StartEndDateFunction } from '../../../../types';
import SelectableRangeCalendar, {
  CalendarState,
} from '../../../../widgets/SelectableRangeCalendar';
import TimeInput from '../../../../widgets/TimeInput';

// Comment #1 (Note about passing a function to setState)
//  See the comment in LogSearchCustomDatetimeRange.tsx

export type Props = {
  onChange: StartEndDateFunction;
  earliest: moment.Moment;
  maxRangeDays: number;
};

type State = {
  available: Array<moment.Moment>;
  latest: moment.Moment;
  calendarState: CalendarState;
  startTime: InputState;
  endTime: InputState;
  title: string;
};

export default class DataCustomDatetimeRange extends React.PureComponent<
  Props,
  State
> {
  constructor(props: Props) {
    super(props);
    const { earliest } = this.props;
    const latest = moment().endOf('day'),
      available = [],
      current = earliest.clone();
    while (current.isSameOrBefore(latest)) {
      available.push(current.clone());
      current.add(1, 'day');
    }
    this.state = {
      available,
      latest,
      calendarState: { start: null, end: null, hovering: null },
      startTime: INITIAL_INPUT_STATE,
      endTime: INITIAL_INPUT_STATE,
      title: this.getTitle(earliest, latest),
    };
  }

  getTitle = (startOfCal: moment.Moment, endOfCal: moment.Moment): string => {
    let title = endOfCal.format('MMM YYYY');
    // If the calendar starts in the previous month (or year!), add that to the title
    if (startOfCal.month() !== endOfCal.month()) {
      title = '-' + title;
      if (startOfCal.year() !== endOfCal.year()) {
        title = startOfCal.format(' YYYY') + title;
      }
      title = startOfCal.format('MMM') + title;
    }
    return title;
  };

  onCalendarChange = (changes: CalendarState) => {
    this.setState((prevState) => ({
      // Building a full state to please TS. See Comment #1 for more. :-(
      ...prevState,
      calendarState: { ...changes },
      ...initializeTimes(
        changes.start,
        prevState.startTime,
        changes.end,
        prevState.endTime,
      ),
    }));
  };

  /**
   * Handles date change by selecting from dropdown
   * (time is ignored and applied later)
   */
  dateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { calendarState, startTime, endTime } = this.state;
    const { start, end } = calendarState;
    const name = event.currentTarget.name;
    const value = moment(event.currentTarget.value, 'YYYYMMDD');
    let update: State = {
      // Building a full state to please TS. See Comment #1 for more. :-(
      ...this.state,
      calendarState: { ...calendarState },
    };

    if (name === 'startdate' || !start || start.isAfter(value)) {
      // set start date
      update.calendarState.start = value;
      if (end && end.isBefore(value)) {
        update.calendarState.end = value;
      }
      // Now initialize the start time (ignore the 'end' params/output)
      const initializeStart = initializeTimes(
        update.calendarState.start,
        startTime,
        null,
        INITIAL_INPUT_STATE,
      ).startTime;
      if (initializeStart) {
        update.startTime = initializeStart;
      }
    }

    if (name === 'enddate') {
      update.calendarState.end = value;
      // We want to initialize the end time, but clamp it to 'now' if the day
      // is 'today'.  initializeTimes will give us the max allowed end time based
      // on the day.
      const maxEnd = initializeTimes(
        null,
        INITIAL_INPUT_STATE,
        update.calendarState.end,
        INITIAL_INPUT_STATE,
      ).endTime;

      if (
        !endTime.value ||
        !endTime.isValid ||
        (maxEnd && maxEnd.value !== END_TIME_INPUT_STATE.value)
      ) {
        update.endTime = maxEnd || END_TIME_INPUT_STATE;
      }
    }

    this.setState(update);
  };

  startTimeChange = (startTime: InputState) => this.setState({ startTime });
  endTimeChange = (endTime: InputState) => this.setState({ endTime });

  apply = () => {
    const { calendarState, startTime, endTime } = this.state;
    const { start, end } = calendarState;
    // parse and re-validate the time
    const startMoment = getValidTime(startTime.value);
    const endMoment = getValidTime(endTime.value);
    if (start && end && startMoment && endMoment) {
      copyDate(start, startMoment);
      copyDate(end, endMoment);
      startMoment.seconds(0).milliseconds(0);
      endMoment.seconds(59).milliseconds(999);
      this.props.onChange(startMoment, endMoment);
    }
  };

  render() {
    const { available, endTime, latest, startTime, calendarState, title } =
      this.state;
    const { earliest, maxRangeDays } = this.props;
    const { start, end } = calendarState;
    return (
      <div className="data-custom-range-picker">
        <SelectableRangeCalendar
          className="px-3 pt-3"
          currentState={calendarState}
          earliestAllowed={earliest}
          latestAllowed={latest}
          onChange={this.onCalendarChange}
          title={<h1>{title}</h1>}
          maxRangeDays={maxRangeDays}
        />
        <div className="horizontal-rule mx-3" />
        <div className="mt-3 d-flex flex-column align-items-center">
          <div>
            Start of Range (Local time):
            <div className="d-flex">
              <Input
                className="mr-2"
                type="select"
                name="startdate"
                value={(start && start.format('YYYYMMDD')) || ''}
                placeholder={'select'}
                onChange={this.dateChange}
              >
                <option disabled hidden value="">
                  Select...
                </option>
                {available.map((day: moment.Moment, index: number) => (
                  <option key={index} value={day.format('YYYYMMDD')}>
                    {day.format('MMM D')}
                  </option>
                ))}
              </Input>
              <TimeInput
                time={startTime}
                onChange={this.startTimeChange}
                resetState={START_TIME_INPUT_STATE}
              />
            </div>
            <div className="invalid-input">
              {!startTime.isValid && <span>Start time is invalid (hh:mm)</span>}
            </div>
          </div>

          <div>
            End of Range (Local time):
            <div className="d-flex">
              <Input
                className="mr-2"
                type="select"
                name="enddate"
                value={(end && end.format('YYYYMMDD')) || ''}
                onChange={this.dateChange}
              >
                <option disabled hidden value="">
                  Select...
                </option>
                {available.map((day: moment.Moment, index: number) => (
                  <option key={index} value={day.format('YYYYMMDD')}>
                    {day.format('MMM D')}
                  </option>
                ))}
              </Input>
              <TimeInput
                time={endTime}
                onChange={this.endTimeChange}
                max={end && moment().isSame(end, 'day') ? moment() : null}
                resetState={END_TIME_INPUT_STATE}
              />
            </div>
            <div className="invalid-input">
              {!endTime.isValid && <span>End time is invalid (hh:mm)</span>}
            </div>
          </div>
        </div>
        <div className="text-center mb-3">
          <Button
            data-cy="apply-custom-range"
            color="primary"
            onClick={this.apply}
            disabled={!startTime.isValid || !endTime.isValid || !start || !end}
          >
            Apply Custom Range
          </Button>
        </div>
      </div>
    );
  }
}
